import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toISO } from "../lib/format";
import { cn } from "@/lib/utils";

interface RangeCalendarProps {
    from: string | null;
    to: string | null;
    max?: string;
    onChange: (from: string | null, to: string | null) => void;
}

const WEEKDAYS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

export function RangeCalendar({ from, to, max, onChange }: RangeCalendarProps) {
    const parse = (s: string | null): Date | null =>
        s ? new Date(s + "T00:00:00") : null;
    const fromD = parse(from);
    const toD = parse(to);
    const maxD = max ? parse(max) : null;

    const [cursor, setCursor] = useState(() => {
        const base = fromD || new Date();
        return new Date(base.getFullYear(), base.getMonth(), 1);
    });
    const [hover, setHover] = useState<Date | null>(null);

    const pickingEnd = !!fromD && !toD;
    const monthLabel = cursor.toLocaleDateString("pt-BR", {
        month: "long",
        year: "numeric",
    });

    const firstDow = new Date(
        cursor.getFullYear(),
        cursor.getMonth(),
        1
    ).getDay();
    const daysInMonth = new Date(
        cursor.getFullYear(),
        cursor.getMonth() + 1,
        0
    ).getDate();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++)
        cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));

    const sameDay = (a: Date | null, b: Date | null) =>
        !!a &&
        !!b &&
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();

    const inRange = (d: Date): boolean => {
        if (!fromD) return false;
        const end = toD || hover;
        if (!end) return false;
        const lo = fromD < end ? fromD : end;
        const hi = fromD < end ? end : fromD;
        return d >= lo && d <= hi;
    };

    const handleClick = (d: Date) => {
        if (maxD && d > maxD) return;
        if (!fromD || (fromD && toD)) {
            onChange(toISO(d), null);
        } else {
            if (d < fromD) onChange(toISO(d), toISO(fromD));
            else onChange(toISO(fromD), toISO(d));
        }
    };

    return (
        <div className="bg-white rounded-xl border border-blue-100 p-3">
            <div className="flex items-center justify-between mb-2">
                <span
                    className={cn(
                        "text-[11px] font-medium",
                        pickingEnd ? "text-blue-600" : "text-gray-400"
                    )}
                >
                    {pickingEnd
                        ? "Clique na data final"
                        : "Clique na data inicial"}
                </span>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() =>
                            setCursor(
                                new Date(
                                    cursor.getFullYear(),
                                    cursor.getMonth() - 1,
                                    1
                                )
                            )
                        }
                        className="p-1 hover:bg-gray-100 rounded-md text-gray-500"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm font-semibold text-gray-800 capitalize min-w-[110px] text-center">
                        {monthLabel}
                    </span>
                    <button
                        type="button"
                        onClick={() =>
                            setCursor(
                                new Date(
                                    cursor.getFullYear(),
                                    cursor.getMonth() + 1,
                                    1
                                )
                            )
                        }
                        className="p-1 hover:bg-gray-100 rounded-md text-gray-500"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-7 gap-0.5 mb-1">
                {WEEKDAYS.map((w) => (
                    <div
                        key={w}
                        className="text-center text-[10px] font-medium text-gray-400 uppercase py-1"
                    >
                        {w}
                    </div>
                ))}
            </div>

            <div
                className="grid grid-cols-7 gap-0.5"
                onMouseLeave={() => setHover(null)}
            >
                {cells.map((d, i) => {
                    if (!d) return <div key={i} />;
                    const disabled = maxD && d > maxD;
                    const isFrom = sameDay(d, fromD);
                    const isTo = sameDay(d, toD);
                    const isEdge = isFrom || isTo;
                    const ranged = inRange(d) && !isEdge;
                    const isToday = sameDay(d, new Date());
                    return (
                        <button
                            key={i}
                            type="button"
                            disabled={!!disabled}
                            onMouseEnter={() => pickingEnd && setHover(d)}
                            onClick={() => handleClick(d)}
                            className={cn(
                                "relative h-8 text-xs font-medium rounded-md transition-colors",
                                disabled && "text-gray-300 cursor-not-allowed",
                                !disabled &&
                                    isEdge &&
                                    "bg-blue-600 text-white font-bold shadow-sm",
                                !disabled &&
                                    !isEdge &&
                                    ranged &&
                                    "bg-blue-100 text-blue-800",
                                !disabled &&
                                    !isEdge &&
                                    !ranged &&
                                    isToday &&
                                    "text-blue-600 font-bold hover:bg-gray-100",
                                !disabled &&
                                    !isEdge &&
                                    !ranged &&
                                    !isToday &&
                                    "text-gray-700 hover:bg-gray-100"
                            )}
                        >
                            {d.getDate()}
                        </button>
                    );
                })}
            </div>

            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                <button
                    type="button"
                    onClick={() => onChange(null, null)}
                    className="text-[11px] font-medium text-gray-400 hover:text-gray-700"
                >
                    Limpar
                </button>
                <button
                    type="button"
                    onClick={() => {
                        const t = toISO(new Date());
                        onChange(t, t);
                    }}
                    className="text-[11px] font-medium text-blue-600 hover:text-blue-800"
                >
                    Hoje
                </button>
            </div>
        </div>
    );
}
